import React from 'react';
import _ from 'lodash';

import CustomSection from '../../custom-general/custom-section';
import Image from '../../../blocks/report-image/image';
import PDFTablePage from '../../../themed/pdf/pdfTablePage';

import { getPCNLAnomalyStyle } from '../../../../utils/utilities';
import { ANOMALY_CHANCE_OF_OCCURRENCE_TYPES, DAMAGE_CLASS_TO_RISK_FACTOR, RISK_RATING_TYPES } from '../../../../utils/constants';
import { Column } from '../../../../utils/models';

import classNames from './style.module.scss';
import riskClassificationChart from '../../../../assets/images/risk_classification_chart.png';

interface Risk {
    riskFactor: string;
    danger: string;
    recommendedCloseoutDue: string;
}

interface ChanceOfOccurrence {
    id: number;
    label: string;
    negligible: string;
    negligibleColor: string;
    minor: string;
    minorColor: string;
    moderate: string;
    moderateColor: string;
    major: string;
    majorColor: string;
    catastrophic: string;
    catastrophicColor: string;
}

const riskColumns: Column[] = [
    {
        path: 'riskFactor',
        label: 'RISK',
        style: (item: Risk) => ({ ...getPCNLAnomalyStyle({ riskFactor: item.riskFactor }), textTransform: 'uppercase' }),
    },
    {
        path: 'danger',
        label: 'DANGER',
        style: (item: Risk) => ({ ...getPCNLAnomalyStyle({ riskFactor: item.riskFactor }), textAlign: 'left'}),
    },
    // {
    //     path: 'recommendedCloseoutDue',
    //     label: 'RECOMMENDED CLOSEOUT DUE',
    //     style: (item: Risk) => ({ ...getPCNLAnomalyStyle({ riskFactor: item.riskFactor }), textAlign: 'left'}),
    // }
];

const riskData: Risk[] = [
        {
            riskFactor: RISK_RATING_TYPES.LOW,
            danger: 'Acceptable - Task may proceed',
            recommendedCloseoutDue: 'No immediate action required'
        },
        {
            riskFactor: RISK_RATING_TYPES.MEDIUM,
            danger: 'Tolerable - Task may proceed with supervision in place',
            recommendedCloseoutDue: 'Three-month period'
        },
        {
            riskFactor: RISK_RATING_TYPES.HIGH,
            danger: 'Unacceptable - Task may not proceed until all controls are in place & supervision is adequate',
            recommendedCloseoutDue: 'One month period'
        },
        {
            riskFactor: RISK_RATING_TYPES.SEVERE,
            danger: 'Unacceptable - Task may not proceed until the risk is reduced',
            recommendedCloseoutDue: 'Immediate action required'
        }
];

const CONSEQUENCES = {
    NEGLIGIBLE: 1,
    MINOR: 2,
    MODERATE: 3,
    MAJOR: 4,
    CATASTROPHIC: 5,
};

const chanceOfOccurenceColumns: Column[] = [
    {
        path: 'label',
        label: '',
        content: (item: ChanceOfOccurrence) => <span>{item.label}:{item.id}</span>,
        style: () => ({ backgroundColor: '#dfe1e6', fontWeight: '900', textAlign: 'left' }),
    },
    {
        path: 'negligible',
        label: 'Negligible:' + CONSEQUENCES.NEGLIGIBLE,
        content: (item: ChanceOfOccurrence) => <span>{item.negligible}:{item.id * CONSEQUENCES.NEGLIGIBLE}</span>,
        style: (item: ChanceOfOccurrence) => ({ backgroundColor: item.negligibleColor }),
    },
    {
        path: 'minor',
        label: 'Minor:' + CONSEQUENCES.MINOR,
        content: (item: ChanceOfOccurrence) => <span>{item.minor}:{item.id * CONSEQUENCES.MINOR}</span>,
        style: (item: ChanceOfOccurrence) => ({ backgroundColor: item.minorColor}),
    },
    {
        path: 'moderate',
        label: 'Moderate:' + CONSEQUENCES.MODERATE,
        content: (item: ChanceOfOccurrence) => <span>{item.moderate}:{item.id * CONSEQUENCES.MODERATE}</span>,
        style: (item: ChanceOfOccurrence) => ({ backgroundColor: item.moderateColor}),
    },
    {
        path: 'major',
        label: 'Major:' + CONSEQUENCES.MAJOR,
        content: (item: ChanceOfOccurrence) => <span>{item.major}:{item.id * CONSEQUENCES.MAJOR}</span>,
        style: (item: ChanceOfOccurrence) => ({ backgroundColor: item.majorColor}),
    },
    {
        path: 'catastrophic',
        label: 'Catastrophic:' + CONSEQUENCES.CATASTROPHIC,
        content: (item: ChanceOfOccurrence) => <span>{item.catastrophic}:{item.id * CONSEQUENCES.CATASTROPHIC}</span>,
        style: (item: ChanceOfOccurrence) => ({ backgroundColor: item.catastrophicColor }),
    } 
]

const chanceOfOccurrenceData: ChanceOfOccurrence[] = [
    {
        id: 1,
        label: ANOMALY_CHANCE_OF_OCCURRENCE_TYPES.RARE,
        negligible: RISK_RATING_TYPES.LOW,
        negligibleColor: DAMAGE_CLASS_TO_RISK_FACTOR.low.color,
        minor: RISK_RATING_TYPES.LOW,
        minorColor: DAMAGE_CLASS_TO_RISK_FACTOR.low.color,
        moderate: RISK_RATING_TYPES.LOW,
        moderateColor: DAMAGE_CLASS_TO_RISK_FACTOR.low.color,
        major: RISK_RATING_TYPES.MEDIUM,
        majorColor: DAMAGE_CLASS_TO_RISK_FACTOR.medium.color,
        catastrophic: RISK_RATING_TYPES.MEDIUM,
        catastrophicColor: DAMAGE_CLASS_TO_RISK_FACTOR.medium.color,
    },
    {
        id: 2,
        label: ANOMALY_CHANCE_OF_OCCURRENCE_TYPES.UNLIKELY,
        negligible: RISK_RATING_TYPES.LOW,
        negligibleColor: DAMAGE_CLASS_TO_RISK_FACTOR.low.color,
        minor: RISK_RATING_TYPES.MEDIUM,
        minorColor: DAMAGE_CLASS_TO_RISK_FACTOR.medium.color,
        moderate: RISK_RATING_TYPES.MEDIUM,
        moderateColor: DAMAGE_CLASS_TO_RISK_FACTOR.medium.color,
        major: RISK_RATING_TYPES.MEDIUM,
        majorColor: DAMAGE_CLASS_TO_RISK_FACTOR.medium.color,
        catastrophic: RISK_RATING_TYPES.MEDIUM,
        catastrophicColor: DAMAGE_CLASS_TO_RISK_FACTOR.medium.color,
    },
    {
        id: 3,
        label: ANOMALY_CHANCE_OF_OCCURRENCE_TYPES.POSSIBLE,
        negligible: RISK_RATING_TYPES.LOW,
        negligibleColor: DAMAGE_CLASS_TO_RISK_FACTOR.low.color,
        minor: RISK_RATING_TYPES.MEDIUM,
        minorColor: DAMAGE_CLASS_TO_RISK_FACTOR.medium.color,
        moderate: RISK_RATING_TYPES.MEDIUM,
        moderateColor: DAMAGE_CLASS_TO_RISK_FACTOR.medium.color,
        major: RISK_RATING_TYPES.HIGH,
        majorColor: DAMAGE_CLASS_TO_RISK_FACTOR.high.color,
        catastrophic: RISK_RATING_TYPES.HIGH,
        catastrophicColor: DAMAGE_CLASS_TO_RISK_FACTOR.high.color,
    },
    {
        id: 4,
        label: ANOMALY_CHANCE_OF_OCCURRENCE_TYPES.LIKELY,
        negligible: RISK_RATING_TYPES.MEDIUM,
        negligibleColor: DAMAGE_CLASS_TO_RISK_FACTOR.medium.color,
        minor: RISK_RATING_TYPES.MEDIUM,
        minorColor: DAMAGE_CLASS_TO_RISK_FACTOR.medium.color,
        moderate: RISK_RATING_TYPES.HIGH,
        moderateColor: DAMAGE_CLASS_TO_RISK_FACTOR.high.color,
        major: RISK_RATING_TYPES.SEVERE,
        majorColor: DAMAGE_CLASS_TO_RISK_FACTOR.severe.color,
        catastrophic: RISK_RATING_TYPES.SEVERE,
        catastrophicColor: DAMAGE_CLASS_TO_RISK_FACTOR.severe.color,
    },
    {
        id: 5,
        label: ANOMALY_CHANCE_OF_OCCURRENCE_TYPES.CERTAINLY,
        negligible: RISK_RATING_TYPES.MEDIUM,
        negligibleColor: DAMAGE_CLASS_TO_RISK_FACTOR.medium.color,
        minor: RISK_RATING_TYPES.MEDIUM,
        minorColor: DAMAGE_CLASS_TO_RISK_FACTOR.medium.color,
        moderate: RISK_RATING_TYPES.HIGH,
        moderateColor: DAMAGE_CLASS_TO_RISK_FACTOR.high.color,
        major: RISK_RATING_TYPES.SEVERE,
        majorColor: DAMAGE_CLASS_TO_RISK_FACTOR.severe.color,
        catastrophic: RISK_RATING_TYPES.SEVERE,
        catastrophicColor: DAMAGE_CLASS_TO_RISK_FACTOR.severe.color,
    },
];

const PCNLRiskClassification: React.FC = () => {
    return (
        <>
            <CustomSection sectionTitle="Anomaly Risk Factor Classification Guide">
                <div className="p-3">
                    <div className={classNames.riskClassificationSection}>
                        <h2>Definitions</h2>

                        <ul>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Acceptable risk:</span> It is the risk that has been reduced to a level that can be tolerated by the Organization having regard to its legal
                                obligations and its own QHS policy.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Communication and Consultation: </span> is a continual and iterative process that an organization conducts to provide, share, or obtain
                                information and to engage in dialogue with stakeholders regarding the management of risk.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Consequence: </span> is the outcome of an event affecting objectives.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Controls: </span> A control is any measure or action that modifies or regulates risk.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Event: </span> is the occurrence or change of a set of circumstances.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Hazard: </span> source with a potential to cause injury and ill health.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Hazard Identification: </span> Process of recognizing that a hazard exists and defining its characteristics.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Hazard Register: </span> A hazard register is a document of identified workplace hazards, their associated risks, and control measures to manage
                                them effectively. Its a risk management tool used to fulfil regulatory requirement.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Health Hazard: </span> Can cause occupational disease or ill-health conditions.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Injury and Ill Health: </span> adverse effect on the physical, mentalm or cognitive condition of a person.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Ionizing Hazard: </span> tasks that can cause radiation.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Level of Risk: </span> is the magnitude of a risk or combination of risks expressed in terms of the combination of consequences and their
                                magnitude.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Likelihood: </span> is the chance of something happening.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Lone Worker: </span> A lone worker is an employee who performs their job duties in isolation from other workers or without direct supervision.
                                They may work remotely, in the field, or during non-standard hours, and they are typically responsible for their own safety and well-being
                                while on duty.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Monitoring: </span> is continual checking, supervising, critically observing or determining the status in order to identify change from the
                                performance level required or expected.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>QHSE: </span> Quality Health Safety & Environment
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Regulatory Requirements: </span> Requirements required by a regulatory body appointed by the state or federal government.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Residual Risk: </span>  is risk remaining after risk treatment.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Review: </span> is the activity undertaken to determine the suitability, adequacy, and effectiveness of the subject matter to achieve established
                                objectives.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Risk: </span> is defined as combination of the likelihood of an occurrence of a hazardous event or exposure(s) and the severity of injury or ill
                                health that can be caused by the event or exposure(s).
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Risk Assessment: </span> is the overall process of risk identification, risk analysis and risk evaluation it can also be defined as the process of
                                evaluating risks arising from hazards taking into account the adequacy of any existing controls and deciding whether or not the risk(s) is
                                acceptable.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Risk Level: </span> Risk level refers to the degree of potential harm or adverse consequences associated with a particular hazard or situation. It
                                is typically assessed based on factors such as the likelihood of occurrence and the severity of potential outcomes. The risk level helps
                                organizations prioritize and implement appropriate control measures to mitigate or manage risks effectively.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Risk Evaluation: </span> is the process of comparing the results of risk analysis with risk criteria to determine whether the risk and/or its magnitude
                                is acceptable or tolerable.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Risk Identification: </span> is the process of finding, recognizing and describing risks.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Risk Management: </span> is the continuous process of identification, assessment, and control of risks. It can also be defined as coordinated
                                activities to direct and control an organization with regards to risk.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Risk Matrix: </span> is used to assess and score risks. Risks are assessed by putting a numerical value on the likelihood that the event will
                                happen and impact on objectives, should that event happen. Risk Score = Likelihood x Impact
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Risk Owner: </span> is a person or entity with the accountability and authority to manage a risk it can also be defined as named persons
                                responsible for overseeing the identification, management, monitoring, and escalation and reporting of a risk.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Risk Register: </span> Risks grouped together on a register for monitoring and reporting purposes.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Statutory Requirements: </span> Requirements required by a state or federal government.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Stakeholder: </span> is a person or organization that can affect, be affected by, or perceive themselves to be affected by a decision or activity
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitleUnderline}>Safety Hazard: </span> Capable of causing immediate physical injury.
                            </li>
                        </ul>
                    </div>
                </div>
            </CustomSection>
            <CustomSection sectionTitle="Anomaly Risk Factor Classification Guide">
                <div className="p-3">
                    <div>
                        <div className={classNames.riskClassificationHeader}>Risk Matrix</div>
                        <PDFTablePage data={riskData} columns={riskColumns} />
                    </div>

                    <div className="mt-5">
                        <div className={classNames.riskClassificationTableHeader}>Consequences</div>
                        <PDFTablePage  data={_.orderBy(chanceOfOccurrenceData, ['id'], 'desc')} columns={chanceOfOccurenceColumns} />
                    </div>

                    <div className={classNames.riskClassificationSection}>
                        <h2>Likelihood</h2>
                        
                        <p>
                            Also called the probability (x-axis) pertains to the extent of how likely it is for the risk to occur. The 5 risk rating levels under this component
                            are as follows.
                        </p>

                        <ul>
                            <li>
                                <span className={classNames.riskClassificationSectionTitle}>Rare - </span> May occur but only in rare and exceptional circumstances.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitle}>Unlikely - </span> Unlikely to occur but could happen possible to happen.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitle}>Moderate - </span> Possible and likely to occur at some time.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitle}>Likely - </span> Likely to occur frequently.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitle}>Certain - </span> Highly likely to occur in most circumstances.
                            </li>
                        </ul>
                    </div>

                    <div className={classNames.riskClassificationSection}>
                        <h2>Consequences</h2>

                        <p>
                            Also called severity the Impact (y-axis) aims to determine the level of effects that the hazard can cause to workplace health and safety.
                            While a 5×5 risk matrix can be tailored to the needs of an organization, the following represent the general terms used to describe the 5
                            levels to determine the risk’s impact:
                        </p>

                        <ul>
                            <li>
                                <span className={classNames.riskClassificationSectionTitle}>Negligible/Insignificant - </span> won’t cause serious injuries or illnesses.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitle}>Minor – </span> can cause injuries or illnesses, only to a mild extent.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitle}>Moderate/Significant – </span> can cause injuries or illnesses that may require medical attention but limited treatment.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitle}>Major - </span> can cause irreversible injuries or illnesses that require constant medical attention.
                            </li>
                            <li>
                                <span className={classNames.riskClassificationSectionTitle}>Severe - </span> can result in fatality.
                            </li>
                        </ul>
                    </div>
                </div>
            </CustomSection>
            <CustomSection sectionTitle="Anomaly Risk Factor Classification Guide">
                <div className="p-3">
                    <Image 
                        src={riskClassificationChart} 
                        alt='HAZARD IDENTIFICATION RISK ASSESSMENT AND CONTROL FLOW CHART' 
                        className={classNames.riskClassificationChart} />
                </div>
            </CustomSection>
        </> 
     );
}
 
export default PCNLRiskClassification;